import React, { useState, useEffect } from 'react';
import { spotlightService, mediaService } from '../../services/api';
import { 
  Plus, Search, Filter, Edit2, Trash2, MapPin, 
  Image as ImageIcon, Globe, Settings, 
  CheckCircle, Clock, Archive, Star,
  X, Save, Loader2, Calendar, AlertCircle
} from 'lucide-react';

function MarketingSpotlight() {
  const [spotlights, setSpotlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSpotlight, setEditingSpotlight] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    link_url: '',
    city: '',
    state: '',
    area: '',
    is_active: true,
    priority: 0,
    starts_at: '',
    ends_at: '',
  });

  useEffect(() => {
    loadSpotlights();
  }, []);

  const loadSpotlights = async () => {
    setLoading(true);
    try {
      const res = await spotlightService.index();
      setSpotlights(res.data || []);
    } catch (error) {
      console.error('Error loading spotlights:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const res = await mediaService.upload(file, 'spotlights');
      setFormData(prev => ({ ...prev, image_url: res.url }));
    } catch (error) {
      alert('Failed to upload image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingSpotlight) {
        await spotlightService.update(editingSpotlight.id, formData);
      } else {
        await spotlightService.store(formData);
      }
      setShowModal(false);
      resetForm();
      loadSpotlights();
      alert('Spotlight saved successfully!');
    } catch (error) {
      alert('Error saving spotlight');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this spotlight?')) {
      try {
        await spotlightService.destroy(id);
        loadSpotlights();
      } catch (error) {
        console.error('Error deleting spotlight:', error);
      }
    }
  };

  const resetForm = () => {
    setEditingSpotlight(null);
    setFormData({
      title: '',
      description: '',
      image_url: '',
      link_url: '',
      city: '',
      state: '',
      area: '',
      is_active: true,
      priority: 0,
      starts_at: '',
      ends_at: '',
    });
  };

  const handleEdit = (item) => {
    setEditingSpotlight(item);
    setFormData({
      title: item.title || '',
      description: item.description || '',
      image_url: item.image_url || '',
      link_url: item.link_url || '',
      city: item.city || '',
      state: item.state || '',
      area: item.area || '',
      is_active: item.is_active,
      priority: item.priority || 0,
      starts_at: item.starts_at ? item.starts_at.split('T')[0] : '',
      ends_at: item.ends_at ? item.ends_at.split('T')[0] : '',
    });
    setShowModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">Local Spotlight</h2>
          <p className="text-slate-400 text-sm">Area-based promotions and local store highlights.</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-xl transition font-medium shadow-lg shadow-red-900/20"
        >
          <Plus size={18} /> New Spotlight
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
          </div>
        ) : spotlights.length === 0 ? (
          <div className="col-span-full text-center py-20 bg-slate-900/50 rounded-3xl border border-slate-800">
            <MapPin className="mx-auto text-slate-700 mb-4" size={48} />
            <p className="text-slate-500">No active spotlights found.</p>
          </div>
        ) : (
          spotlights.map((item) => (
            <div key={item.id} className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden group hover:border-red-500/50 transition-all">
              <div className="aspect-video relative overflow-hidden bg-slate-800">
                {item.image_url ? (
                  <img src={item.image_url} alt={item.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-600">
                    <ImageIcon size={32} />
                  </div>
                )}
                <div className="absolute top-4 left-4">
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${
                    item.is_active ? 'bg-red-500/10 text-red-500 border-red-500/20' : 'bg-slate-500/10 text-slate-500 border-slate-500/20'
                  }`}>
                    {item.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="p-5 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-slate-100 group-hover:text-red-400 transition-colors">{item.title}</h3>
                    <div className="flex items-center gap-1.5 text-slate-500 text-xs mt-1">
                      <MapPin size={12} />
                      {item.area ? `${item.area}, ` : ''}{item.city}, {item.state}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleEdit(item)} className="p-2 text-slate-400 hover:text-blue-400 transition-colors">
                      <Edit2 size={16} />
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="p-2 text-slate-400 hover:text-red-400 transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{item.description}</p>
                <div className="pt-3 flex items-center justify-between border-t border-slate-800">
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                    <Calendar size={12} />
                    {item.starts_at ? new Date(item.starts_at).toLocaleDateString() : 'Immediate'}
                  </div>
                  <div className="text-[10px] font-black text-slate-100">
                    PRIORITY {item.priority}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => !submitting && setShowModal(false)} />
          <div className="relative bg-slate-900 border border-slate-800 w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-3xl flex flex-col shadow-2xl">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-100">{editingSpotlight ? 'Edit Spotlight' : 'New Local Spotlight'}</h3>
              <button onClick={() => !submitting && setShowModal(false)} className="p-2 text-slate-500 hover:text-slate-300 transition"><X size={20} /></button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-1.5">Spotlight Title</label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g. Pune Store Anniversary"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-1.5">Description</label>
                    <textarea
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Highlight what makes this special..."
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 focus:outline-none focus:border-red-500 transition-colors resize-none"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                   <label className="block text-sm font-semibold text-slate-300 mb-1.5">Promotion Image</label>
                   <div className="relative aspect-video rounded-2xl bg-slate-950 border-2 border-dashed border-slate-800 flex flex-col items-center justify-center overflow-hidden group hover:border-red-500/50 transition-all cursor-pointer">
                    {formData.image_url ? (
                      <>
                        <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-slate-950/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                          <p className="text-white text-xs font-bold">Replace Asset</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <ImageIcon className="text-slate-600 mb-2" size={32} />
                        <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest text-center px-4">Upload Banner Image</p>
                      </>
                    )}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-1.5">Area / Neighborhood</label>
                  <input
                    type="text"
                    value={formData.area}
                    onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                    placeholder="e.g. Koregaon Park"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-1.5">City</label>
                  <input
                    type="text"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g. Pune"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-1.5">State</label>
                  <input
                    type="text"
                    required
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="e.g. Maharashtra"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-1.5">Start Date</label>
                  <input
                    type="date"
                    value={formData.starts_at}
                    onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-1.5">End Date</label>
                  <input
                    type="date"
                    value={formData.ends_at}
                    onChange={(e) => setFormData({ ...formData, ends_at: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-950 border border-slate-800 rounded-2xl">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl ${formData.is_active ? 'bg-red-500/10 text-red-500' : 'bg-slate-800 text-slate-500'}`}>
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-100">Status</p>
                    <p className="text-[10px] text-slate-500 font-medium">Toggle spotlight visibility</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  className={`w-12 h-6 rounded-full transition-colors relative ${formData.is_active ? 'bg-red-600' : 'bg-slate-700'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.is_active ? 'left-7' : 'left-1'}`} />
                </button>
              </div>
            </form>

            <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-end gap-3 bg-slate-900/50">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-6 py-2.5 rounded-xl border border-slate-800 text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition font-medium"
              >
                Discard
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !formData.title}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-2.5 rounded-xl transition font-bold shadow-lg shadow-red-900/20 disabled:opacity-50"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                {editingSpotlight ? 'Update Spotlight' : 'Publish Spotlight'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MarketingSpotlight;
